import React, { useEffect, useRef } from 'react';
import { userActionLogger } from '../../lib/userActionLogger';

interface AutoTrackerProps {
  children: React.ReactNode;
  trackClicks?: boolean;
  trackScrolls?: boolean;
  trackFormSubmissions?: boolean;
  trackDownloads?: boolean;
  trackErrors?: boolean;
  trackConsoleErrors?: boolean;
  excludeSelectors?: string[];
  includeSelectors?: string[];
  debounceMs?: number;
}

const MIN_DEBOUNCE_MS = 100;
const MAX_DEBOUNCE_MS = 5000;

export const AutoTracker: React.FC<AutoTrackerProps> = ({
  children,
  trackClicks = true,
  trackScrolls = true,
  trackFormSubmissions = true,
  trackDownloads = true,
  trackErrors = true,
  trackConsoleErrors = true,
  excludeSelectors = [],
  includeSelectors = [],
  debounceMs = 100
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
   
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastScrollY = useRef<number>(0);
  const safeDebounceMs = Math.min(Math.max(debounceMs, MIN_DEBOUNCE_MS), MAX_DEBOUNCE_MS);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const cleanupFunctions: (() => void)[] = [];

    // Auto-track clicks
    if (trackClicks) {
      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        
        // Check exclude/include selectors
        if (excludeSelectors.some(selector => target.matches(selector))) return;
        if (includeSelectors.length > 0 && !includeSelectors.some(selector => target.matches(selector))) return;

        const elementInfo = getElementInfo(target);
        
        userActionLogger.click(target, {
          ...elementInfo,
          mouse_position: { x: event.clientX, y: event.clientY }
        });
      };

      container.addEventListener('click', handleClick);
      cleanupFunctions.push(() => container.removeEventListener('click', handleClick));
    }

    // Auto-track form submissions
    if (trackFormSubmissions) {
      const handleSubmit = async (event: SubmitEvent) => {
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        // Only send field names, never values — avoids PII leakage
        const fieldNames = Object.keys(Object.fromEntries(formData.entries()));

        try {
          await userActionLogger.formSubmit(
            form.id || form.name || 'unnamed_form',
            { fields: fieldNames },
            true
          );
        } catch (error) {
          await userActionLogger.formSubmit(
            form.id || form.name || 'unnamed_form',
            { fields: fieldNames },
            false,
            error instanceof Error ? error.message : 'Form submission tracking failed'
          );
        }
      };

      container.addEventListener('submit', handleSubmit);
      cleanupFunctions.push(() => container.removeEventListener('submit', handleSubmit));
    }

    // Auto-track downloads
    if (trackDownloads) {
      const handleDownload = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const link = target.closest('a[href]') as HTMLAnchorElement;
        
        if (link && isDownloadLink(link)) {
          const fileName = getFileNameFromUrl(link.href) || link.download;
          
          userActionLogger.download(
            link.href,
            fileName,
            {
              file_extension: getFileExtension(fileName || link.href),
              link_text: link.textContent?.trim()
            }
          );
        }
      };

      container.addEventListener('click', handleDownload);
      cleanupFunctions.push(() => container.removeEventListener('click', handleDownload));
    }

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [trackClicks, trackFormSubmissions, trackDownloads, excludeSelectors, includeSelectors]);

  // Auto-track scrolling (global)
  useEffect(() => {
    if (!trackScrolls) return;

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const currentScrollY = window.scrollY;
        const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
        const scrollPercentage = Math.round((currentScrollY / Math.max(document.body.scrollHeight - window.innerHeight, 1)) * 100);

        userActionLogger.log({
          action_type: 'scroll',
          item_type: 'page',
          item_id: window.location.pathname,
          metadata: {
            scroll_direction: scrollDirection,
            scroll_percentage: scrollPercentage,
            scroll_position: currentScrollY
          }
        });

        lastScrollY.current = currentScrollY;
      }, safeDebounceMs);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [trackScrolls, safeDebounceMs]);

  // Auto-track errors
  useEffect(() => {
    if (!trackErrors) return;

    const handleError = (event: ErrorEvent) => {
      userActionLogger.error(
        'javascript_error',
        event.message,
        {
          filename: event.filename,
          line_number: event.lineno,
          column_number: event.colno,
          stack: event.error?.stack
        }
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      userActionLogger.error(
        'unhandled_promise_rejection',
        event.reason?.toString() || 'Unknown promise rejection',
        {
          rejection_reason: event.reason
        }
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [trackErrors]);

  // Auto-track console.error calls
  useEffect(() => {
    if (!trackConsoleErrors) return;

    const originalConsoleError = console.error;
    const recentHashes = new Map<string, number>();
    let isLogging = false;

    const DEDUP_WINDOW_MS = 5000;
    const MAX_MESSAGE_LENGTH = 5000;

    console.error = (...args: unknown[]) => {
      // Always call original first
      originalConsoleError.apply(console, args);

      // Recursion guard
      if (isLogging) return;

      const message = args
        .map((arg) => {
          if (typeof arg === 'string') return arg;
          try { return JSON.stringify(arg); } catch { return String(arg); }
        })
        .join(' ')
        .slice(0, MAX_MESSAGE_LENGTH);

      // Skip our own logger messages to prevent infinite loops
      if (message.startsWith('[UserActionLogger]')) return;

      // Dedup: skip if same message seen in last 5 seconds
      const hash = message.slice(0, 200);
      const now = Date.now();
      const lastSeen = recentHashes.get(hash);
      if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return;
      recentHashes.set(hash, now);

      // Clean up old entries periodically
      if (recentHashes.size > 100) {
        for (const [key, timestamp] of recentHashes) {
          if (now - timestamp > DEDUP_WINDOW_MS) recentHashes.delete(key);
        }
      }

      isLogging = true;
      userActionLogger.error('console_error', message).finally(() => {
        isLogging = false;
      });
    };

    return () => {
      console.error = originalConsoleError;
    };
  }, [trackConsoleErrors]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      {children}
    </div>
  );
};

// Helper functions
function getElementInfo(element: HTMLElement) {
  const tagName = element.tagName.toLowerCase();
  const id = element.id;
  const className = element.className;
  const textContent = element.textContent?.trim().substring(0, 100);
  const dataAttributes = getDataAttributes(element);

  return {
    tag_name: tagName,
    element_id: id,
    class_name: className,
    text_content: textContent,
    data_attributes: dataAttributes,
    has_onclick: element.hasAttribute('onclick'),
    aria_label: element.getAttribute('aria-label'),
    role: element.getAttribute('role')
  };
}

function getDataAttributes(element: HTMLElement): Record<string, string> {
  const dataAttrs: Record<string, string> = {};
  
  for (const attr of element.attributes) {
    if (attr.name.startsWith('data-')) {
      dataAttrs[attr.name] = attr.value;
    }
  }
  
  return dataAttrs;
}

function isDownloadLink(link: HTMLAnchorElement): boolean {
  // Check if it has download attribute
  if (link.hasAttribute('download')) return true;
  
  // Check common file extensions
  const downloadExtensions = [
    '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
    '.zip', '.rar', '.tar', '.gz', '.csv', '.txt', '.json',
    '.xml', '.mp3', '.mp4', '.avi', '.mov', '.jpg', '.jpeg',
    '.png', '.gif', '.svg', '.ico', '.dmg', '.exe', '.msi'
  ];
  
  const href = link.href.toLowerCase();
  return downloadExtensions.some(ext => href.includes(ext));
}

function getFileNameFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.split('/').pop();
    return fileName && fileName.includes('.') ? fileName : null;
  } catch {
    return null;
  }
}

function getFileExtension(fileName: string): string | null {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot !== -1 ? fileName.substring(lastDot + 1).toLowerCase() : null;
}

export default AutoTracker;